import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  contrastText,
  CURATED_HEX_COLORS,
  formatHSB,
  hsbToCss,
  makeColorTargets,
  scoreColor,
} from '../gameslib/color'
import { REMEMBER_SECONDS } from '../gameslib/difficulty'
import { useGameSettings } from '../gameslib/gameSettings'
import { useTutorial } from '../gameslib/useTutorial'
import { sfxSubmit, sfxTick, sfxWhoosh } from '../gameslib/audio'
import { Countdown, MemoryTimer } from '../gamesshared/Countdown'
import { GameShell, IntroScreen, PlayerBanner } from '../gamesshared/IntroScreen'
import { PassDevice, PlayerSetup, WinnerScreen } from '../gamesshared/Multiplayer'
import { RoundResult, TotalScreen } from '../gamesshared/RoundResult'
import { TutorialCoach } from '../gamesshared/TutorialCoach'

function HsbStrips({ value, onChange, onActive, stripsRef }) {
  const setFromPointer = (channel, el, clientY) => {
    const rect = el.getBoundingClientRect()
    const t = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height))
    let next = value
    if (channel === 'h') next = { ...value, h: Math.round(t * 360) }
    if (channel === 's') next = { ...value, s: Math.round((1 - t) * 100) }
    if (channel === 'b') next = { ...value, b: Math.round((1 - t) * 100) }
    onChange(next)
    onActive?.(channel)
    void sfxTick()
  }

  const bind = (channel) => ({
    onPointerDown: (e) => {
      e.currentTarget.setPointerCapture(e.pointerId)
      onActive?.(channel)
      setFromPointer(channel, e.currentTarget, e.clientY)
    },
    onPointerMove: (e) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
      setFromPointer(channel, e.currentTarget, e.clientY)
    },
  })

  const hueGrad =
    'linear-gradient(#ff0000 0%, #ff8000 8.333%, #ffff00 16.667%, #80ff00 25%, #00ff00 33.333%, #00ff80 41.667%, #00ffff 50%, #0080ff 58.333%, #0000ff 66.667%, #8000ff 75%, #ff00ff 83.333%, #ff0080 91.667%, #ff0000 100%)'
  const satGrad = `linear-gradient(${hsbToCss({ h: value.h, s: 100, b: Math.max(value.b, 35) })}, ${hsbToCss({ h: value.h, s: 0, b: Math.max(value.b, 35) })})`
  const briGrad = `linear-gradient(${hsbToCss({ h: value.h, s: value.s, b: 100 })}, #000000)`

  return (
    <div className="hsb-strips" aria-label="HSB controls" ref={stripsRef}>
      <div className="hsb-strips__strip" role="slider" aria-label="Hue" aria-valuenow={value.h} style={{ background: hueGrad }} {...bind('h')}>
        <span className="hsb-strips__handle" style={{ top: `${(value.h / 360) * 100}%` }} />
      </div>
      <div className="hsb-strips__strip" role="slider" aria-label="Saturation" aria-valuenow={value.s} style={{ background: satGrad }} {...bind('s')}>
        <span className="hsb-strips__handle" style={{ top: `${100 - value.s}%` }} />
      </div>
      <div className="hsb-strips__strip" role="slider" aria-label="Brightness" aria-valuenow={value.b} style={{ background: briGrad }} {...bind('b')}>
        <span className="hsb-strips__handle" style={{ top: `${100 - value.b}%` }} />
      </div>
    </div>
  )
}

function TargetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <path d="M12 2.5v3.2M12 18.3v3.2M2.5 12h3.2M18.3 12h3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}

function colorCoach(step) {
  if (step === 'watch') return { message: 'Memorize this color', gesture: 'watch', anchor: 'center' }
  if (step === 'drag') return { message: 'Drag a strip', gesture: 'drag', anchor: 'strips' }
  if (step === 'submit') return { message: 'Tap to lock in', gesture: 'tap', anchor: 'submit' }
  if (step === 'done') return { message: 'Closer = higher score', gesture: 'tap', anchor: 'submit' }
  return null
}

export function ColorGame() {
  const { rounds, setRounds, setModeLabel } = useGameSettings()
  const tutorial = useTutorial('color')
  const [phase, setPhase] = useState('intro')
  const [difficulty, setDifficulty] = useState('easy')
  const [round, setRound] = useState(0)
  const [targets, setTargets] = useState([])
  const [guess, setGuess] = useState({ h: 120, s: 70, b: 70 })
  const [scores, setScores] = useState([])
  const [lastScore, setLastScore] = useState(0)
  const [activeChannel, setActiveChannel] = useState('b')
  const startGuess = useRef(null)
  const stripsRef = useRef(null)
  const submitRef = useRef(null)
  const nextRef = useRef(null)

  const [playMode, setPlayMode] = useState('solo')
  const [players, setPlayers] = useState([])
  const [playerIndex, setPlayerIndex] = useState(0)

  const target = targets[round]
  const rememberS = tutorial.active ? Math.max(REMEMBER_SECONDS.easy, 3.5) : REMEMBER_SECONDS[difficulty]
  const currentPlayer = players[playerIndex]
  const playRounds = tutorial.active ? 1 : rounds

  useEffect(() => {
    setModeLabel(playMode === 'multi' ? 'multi' : 'solo')
  }, [playMode, setModeLabel])

  const resetTurn = (sharedTargets = targets) => {
    setScores([])
    setRound(0)
    setGuess({ h: 120, s: 70, b: 70 })
    setLastScore(0)
    if (sharedTargets?.length) setTargets(sharedTargets)
  }

  const makeTargets = (count = rounds) => makeColorTargets(count, CURATED_HEX_COLORS.length)

  const beginPlay = (asTutorial) => {
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
    if (asTutorial) tutorial.start('watch')
    else if (tutorial.active) tutorial.finish()
    const nextTargets = makeTargets(asTutorial ? 1 : rounds)
    setTargets(nextTargets)
    resetTurn(nextTargets)
    setPhase('countdown')
  }

  const startSolo = () => beginPlay(tutorial.needsTutorial)
  const startTeach = () => beginPlay(true)

  const exitTutorial = () => {
    tutorial.skip()
    setPhase('intro')
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
  }

  const openMulti = () => {
    if (tutorial.active) tutorial.finish()
    setPlayMode('multi')
    setPhase('setup')
  }

  const confirmPlayers = (list) => {
    const nextTargets = makeTargets()
    setTargets(nextTargets)
    setPlayers(list.map((p) => ({ ...p, scores: [] })))
    setPlayerIndex(0)
    resetTurn(nextTargets)
    setPhase('pass')
  }

  const beginPlayerTurn = () => {
    resetTurn(targets)
    setPhase('countdown')
  }

  const finishPlayerTurn = (finalScores) => {
    if (playMode === 'solo') {
      setPhase('total')
      return
    }
    const updated = players.map((p, i) => (i === playerIndex ? { ...p, scores: finalScores } : p))
    setPlayers(updated)
    if (playerIndex >= players.length - 1) {
      setPhase('winner')
    } else {
      setPlayerIndex((i) => i + 1)
      setPhase('pass')
    }
  }

  const onCountdownDone = useCallback(() => {
    if (tutorial.active) tutorial.go('watch')
    setPhase('stimulus')
  }, [tutorial])

  const onStimulusDone = useCallback(() => {
    if (tutorial.active) tutorial.go('drag')
    setPhase('input')
  }, [tutorial])

  useEffect(() => {
    if (phase === 'stimulus') void sfxWhoosh()
  }, [phase, round])

  useEffect(() => {
    if (phase === 'input') startGuess.current = { ...guess }
    // only seed when entering input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  const onGuessChange = (next) => {
    setGuess(next)
    if (tutorial.active && tutorial.step === 'drag' && startGuess.current) {
      const d =
        Math.abs(next.h - startGuess.current.h) +
        Math.abs(next.s - startGuess.current.s) +
        Math.abs(next.b - startGuess.current.b)
      if (d > 8) tutorial.go('submit')
    }
  }

  const submit = () => {
    if (tutorial.active && tutorial.step === 'drag') return
    void sfxSubmit()
    const s = scoreColor(target, guess)
    setLastScore(s)
    setScores((prev) => [...prev, s])
    if (tutorial.active) tutorial.go('done')
    setPhase('result')
  }

  const next = () => {
    if (tutorial.active) {
      tutorial.finish()
      setPhase('intro')
      return
    }
    if (round + 1 >= playRounds) {
      const finalScores = scores.length >= playRounds ? scores : [...scores, lastScore]
      finishPlayerTurn(finalScores)
    } else {
      setRound((r) => r + 1)
      setGuess({ h: 180, s: 50, b: 60 })
      setPhase('countdown')
    }
  }

  const scoreInk = useMemo(() => (guess ? contrastText(guess) : '#000'), [guess])
  const backHome = () => {
    if (tutorial.active) tutorial.finish()
    setPhase('intro')
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
  }

  const roundWord = rounds === 1 ? 'color' : 'colors'
  const coach = tutorial.active ? colorCoach(tutorial.step) : null
  const coachTarget =
    tutorial.step === 'done'
      ? nextRef
      : tutorial.step === 'drag'
        ? stripsRef
        : tutorial.step === 'submit'
          ? submitRef
          : null

  return (
    <GameShell>
      {phase === 'intro' && (
        <IntroScreen
          title="color"
          blurb={`Humans can't reliably recall colors. This is a simple game to see how good (or bad) you are at it.\nWe'll show you ${rounds} ${roundWord}, then you'll try and recreate them.`}
          onSolo={startSolo}
          onMulti={openMulti}
          onTeach={startTeach}
          difficulty={difficulty}
          onDifficulty={setDifficulty}
          rounds={rounds}
          onRounds={setRounds}
        />
      )}

      {phase === 'setup' && (
        <PlayerSetup onConfirm={confirmPlayers} onBack={() => setPhase('intro')} rounds={rounds} onRounds={setRounds} />
      )}

      {phase === 'pass' && currentPlayer && (
        <PassDevice
          player={currentPlayer}
          index={playerIndex}
          total={players.length}
          rounds={rounds}
          onReady={beginPlayerTurn}
        />
      )}

      {phase === 'countdown' && (
        <div className="stage">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <Countdown onDone={onCountdownDone} />
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} />}
        </div>
      )}

      {phase === 'stimulus' && target && (
        <div className="stage" style={{ background: hsbToCss(target), color: contrastText(target) }}>
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <MemoryTimer seconds={rememberS} onDone={onStimulusDone} />
          <div className="watermark">Memory</div>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} />}
        </div>
      )}

      {phase === 'input' && (
        <div className="color-input">
          <HsbStrips value={guess} onChange={onGuessChange} onActive={setActiveChannel} stripsRef={stripsRef} />
          <div className="color-input__preview" style={{ background: hsbToCss(guess), color: contrastText(guess) }}>
            {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
            {!tutorial.active && <div className="round-pill">{round + 1} / {playRounds}</div>}
            <div className="color-input__channel">
              {activeChannel === 'h' ? 'HUE' : activeChannel === 's' ? 'SATURATION' : 'BRIGHTNESS'}
            </div>
            <div className="watermark">Memory</div>
            <button
              type="button"
              ref={submitRef}
              className={`go-btn color-input__go${tutorial.step === 'submit' ? ' is-tutor-pulse' : ''}`}
              aria-label="Submit"
              onClick={submit}
              disabled={tutorial.active && tutorial.step === 'drag'}
            >
              <TargetIcon />
            </button>
          </div>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />}
        </div>
      )}

      {phase === 'result' && target && (
        <div className="screen">
          <RoundResult
            score={lastScore}
            round={tutorial.active ? 1 : round + 1}
            totalRounds={playRounds}
            onNext={next}
            nextButtonRef={nextRef}
            scoreColor={scoreInk}
          >
            <div className="round-result__half" style={{ background: hsbToCss(guess), color: contrastText(guess) }}>
              <span className="round-result__label">Your selection</span>
              <strong className="round-result__hsb">{formatHSB(guess)}</strong>
            </div>
            <div className="round-result__half" style={{ background: hsbToCss(target), color: contrastText(target) }}>
              <span className="round-result__label">Original</span>
              <strong className="round-result__hsb">{formatHSB(target)}</strong>
            </div>
          </RoundResult>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />}
        </div>
      )}

      {phase === 'total' && <TotalScreen scores={scores} onReplay={startSolo} />}

      {phase === 'winner' && (
        <WinnerScreen
          players={players}
          onReplay={() => {
            const nextTargets = makeTargets()
            setTargets(nextTargets)
            setPlayers((prev) => prev.map((p) => ({ ...p, scores: [] })))
            setPlayerIndex(0)
            resetTurn(nextTargets)
            setPhase('pass')
          }}
          onHome={backHome}
        />
      )}
    </GameShell>
  )
}
